<style lang="scss">
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/utils";
  @use "../../../assets/css/design-system/mixins";

  .file-upload {
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(16px);

    .upload-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: utils.in_rem(8px);
      border: 2px dashed colors.get(interfaceSwGrey_10);
      border-radius: utils.in_rem(8px);
      padding: utils.in_rem(32px);
      transition:
        border-color 0.2s,
        background-color 0.2s;
      cursor: pointer;

      &.drag-over {
        border-color: colors.get(primary_50);
        @include colors.background-color(primary_5);
      }

      &:hover:not(.disabled) {
        @include colors.background-color(interfaceSwGrey_5);
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }

      .upload-text {
        @include mixins.body2();
        @include colors.color(textLighterBlack);
        text-align: center;
      }

      .upload-subtext {
        @include mixins.body3();
        @include colors.color(textLighterBlack);
        text-align: center;
      }

      input[type="file"] {
        display: none;
      }
    }

    .selected-files {
      display: flex;
      flex-direction: column;
      gap: utils.in_rem(8px);
      max-height: utils.in_rem(200px);
      overflow-y: auto;

      .file-item {
        @include colors.background-color(interfaceSwGrey_5);
        display: flex;
        align-items: center;
        gap: utils.in_rem(12px);
        padding: utils.in_rem(12px);
        border-radius: utils.in_rem(6px);

        .file-details {
          flex: 1;

          .file-name {
            @include mixins.body2();
            @include colors.color(textBlack);
            word-break: break-word;
          }

          .file-size {
            @include mixins.body3();
            @include colors.color(textLighterBlack);
          }
        }
      }
    }

    .error-message {
      @include colors.color(systemError_100);
      @include mixins.body3();
      text-align: center;
    }
  }
</style>

<script>
  import Button from "../../atoms/Button.svelte";
  import Icon from "../../atoms/Icon.svelte";

  let {
    maxFiles = 1,
    allowedTypes = [],
    maxFileSize = Infinity,
    acceptedExtensions = [],
    uploadText = "Drop your files here or click to browse",
    uploadSubtext = "Select files to upload",
    invalidTypeError = "Invalid file type",
    fileTooLargeError = "File size too large",
    showSelectedFiles = true,
    isUploading = false,
    selectedFiles = $bindable([]),
    onFilesSelected = () => {},
  } = $props();

  let dragOver = $state(false);
  let errorMessage = $state("");
  let fileInput = $state();

  const hasFiles = $derived(selectedFiles.length > 0);
  const isMultiple = $derived(maxFiles > 1);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";

    const divisor = 1024;
    const units = ["Bytes", "KB", "MB", "GB", "TB"];
    let unitIndex = 0;
    let sizeValue = bytes;

    while (sizeValue >= divisor && unitIndex < units.length - 1) {
      sizeValue /= divisor;
      unitIndex += 1;
    }

    // Round to 1 decimal place for values >= 1, otherwise show 2 decimal places
    const decimals = sizeValue >= 1 ? 1 : 2;
    return `${sizeValue.toFixed(decimals)} ${units[unitIndex]}`;
  };

  const validateFile = (
    file,
    allowedTypes = [],
    maxFileSize = Infinity,
    allowedTypesError = "Invalid file type",
    maxFileSizeError = "File size too large"
  ) => {
    const typeError =
      allowedTypes.length > 0 && !allowedTypes.includes(file.type)
        ? allowedTypesError
        : null;
    const sizeError = file.size > maxFileSize ? maxFileSizeError : null;
    return typeError ?? sizeError ?? null;
  };

  const handleFileSelect = (files) => {
    errorMessage = "";
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (allowedTypes.length > 0 || maxFileSize < Infinity) {
        const validationError = validateFile(
          file,
          allowedTypes,
          maxFileSize,
          invalidTypeError,
          fileTooLargeError
        );

        if (validationError) {
          errors.push(`${file.name}: ${validationError}`);
          return;
        }
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      errorMessage = errors.join(", ");
    }

    if (validFiles.length > 0) {
      if (isMultiple) {
        const availableSlots = maxFiles - selectedFiles.length;
        const filesToAdd = validFiles.slice(0, availableSlots);
        selectedFiles = [...selectedFiles, ...filesToAdd];

        if (validFiles.length > availableSlots) {
          errorMessage = `Only ${availableSlots} file(s) were added. Maximum is ${maxFiles} files.`;
        }
      } else {
        selectedFiles = [validFiles[0]];
      }

      onFilesSelected({
        files: isMultiple ? selectedFiles : selectedFiles[0],
        validFiles,
        errors,
      });
    }
  };

  const handleFileInput = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    dragOver = false;

    if (!isUploading) {
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length > 0) {
        handleFileSelect(files);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isUploading) {
      dragOver = true;
    }
  };

  const handleDragLeave = (event) => {
    // Only set dragOver to false if we're leaving the upload area entirely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      dragOver = false;
    }
  };

  const removeFile = (index) => {
    selectedFiles = selectedFiles.filter((_, i) => i !== index);

    onFilesSelected({
      files: isMultiple ? selectedFiles : selectedFiles[0] || null,
    });

    if (fileInput) {
      fileInput.value = "";
    }
  };

  const openFileSelector = () => {
    if (!isUploading) {
      fileInput?.click();
    }
  };

  const handleKeydown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      openFileSelector();
    }
  };
</script>

<div class="file-upload">
  {#if !hasFiles || isMultiple}
    <div
      class="upload-area"
      class:drag-over={dragOver && !isUploading}
      class:disabled={isUploading}
      ondrop={handleDrop}
      ondragover={handleDragOver}
      ondragleave={handleDragLeave}
      role="button"
      tabindex="0"
      onclick={openFileSelector}
      onkeydown={handleKeydown}
      aria-label="Upload files"
    >
      <Icon
        class="fa-regular fa-cloud-arrow-up color-textLighterBlack"
        size="xl"
      />
      <div class="upload-text">{uploadText}</div>
      <div class="upload-subtext">{uploadSubtext}</div>

      <input
        bind:this={fileInput}
        type="file"
        multiple={isMultiple}
        accept={acceptedExtensions.join(",")}
        onchange={handleFileInput}
      />
    </div>
  {/if}

  {#if showSelectedFiles && hasFiles}
    <div class="selected-files">
      {#each selectedFiles as file, index}
        <div class="file-item">
          <Icon
            class="fa-regular fa-file color-textLighterBlack"
            size="small"
          />
          <div class="file-details">
            <div class="file-name">{file.name}</div>
            <div class="file-size">{formatFileSize(file.size)}</div>
          </div>
          <Button
            type="button"
            kind="ghost-grey"
            size="xsmall"
            disabled={isUploading}
            class={isUploading ? "disabled" : ""}
            onclick={() => removeFile(index)}
          >
            Remove
          </Button>
        </div>
      {/each}
    </div>
  {/if}

  {#if errorMessage}
    <div class="error-message" role="alert" aria-live="polite">
      {errorMessage}
    </div>
  {/if}
</div>
